import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Image, Platform} from 'react-native';
import {Dimensions, Modal} from 'react-native';
import {StyleSheet, View} from 'react-native';
import AVText from 'src/components/elements/AVText';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import RewardSquareIcon from 'src/components/img/svg/rewards/RewardSquareIcon';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import {BuildTypeEnum} from 'src/constants/BuildTypeEnum';
import Settings from 'src/Settings';
import PersistentStore from 'src/services/PersistentStoreService';
import AccountStore from 'src/stores/AccountStore';

const screenWidth =
  Platform.OS === 'web'
    ? Dimensions.get('window').width / 2
    : Dimensions.get('window').width;
const modalWidth = screenWidth * 0.9;

type MoreWaysToEarnDetailsDialogueProps = {
  exploreNowAction?: () => void;
};

const MoreWaysToEarnDetailsDialogue = (
  props: MoreWaysToEarnDetailsDialogueProps,
) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  useEffect(() => {
    checkModalStatus();
  }, []);

  const checkModalStatus = async () => {
    const rewardPolicies = AccountStore.getRewardMoreWaysToEarn();
    const rewardModalStatus =
      await PersistentStore.getRewardMoreWaysToEarnStatus();
    if (rewardPolicies.length > 0 && !rewardModalStatus) {
      setIsModalVisible(true);
    }
  };

  const modalClose = useCallback(() => {
    setIsModalVisible(false);
    PersistentStore.setRewardMoreWaysToEarnStatus(true);
  }, []);

  const onExploreNowClick = useCallback(() => {
    modalClose();
    props.exploreNowAction();
  }, []);

  const getImageSource = useMemo(() => {
    switch (Settings.buildType) {
      case BuildTypeEnum.canteen:
        return require('src/components/img/reward/MoreWaysToEarnCanteen.png');
      case BuildTypeEnum.companykitchen:
        return require('src/components/img/reward/MoreWaysToEarnCK.png');
      case BuildTypeEnum.refive:
        return require('src/components/img/reward/MoreWaysToEarnRefive.png');
      case BuildTypeEnum.aramark:
        return require('src/components/img/reward/MoreWaysToEarnAramark.png');
      default:
        return require('src/components/img/reward/MoreWaysToEarnDefault.png');
    }
  }, []);

  return (
    <Modal
      visible={isModalVisible}
      transparent={true}
      statusBarTranslucent={true}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalView}>
          <AVTouchableOpacity
            accessible={true}
            accessibilityLabel={Localized.Buttons.cancel}
            accessibilityRole="button"
            aria-label={Localized.Buttons.cancel}
            role="button"
            style={styles.cancelButton}
            onPress={modalClose}
            hitSlop={Styles.HitSlop_10}
            testID={'qa_ClosePopup_btn'}
          >
            <CancelIcon />
          </AVTouchableOpacity>
          <View style={styles.topTitleView}>
            <AVText
              style={styles.titleText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
            >
              {Localized.Labels.introducing_more_ways_to_earn}
            </AVText>
            <View style={styles.iconView}>
              <RewardSquareIcon
                color={getLayoutDescriber().bottomTabBtnColor()}
              />
            </View>
            <AVText
              style={styles.subTitleText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
            >
              {Localized.Labels.complete_and_earn}
            </AVText>
            <AVText
              style={styles.descriptionText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
            >
              {Localized.Labels.you_can_now_earn_more_points_description}
            </AVText>
            <View style={styles.iconView}>
              <Image
                style={styles.image}
                resizeMode="contain"
                source={getImageSource}
              />
            </View>

            <RoundedAppButton
              titleText={Localized.Labels.explore_now}
              accessibilityLabelValue={Localized.Buttons.cancel}
              aria-label={Localized.Buttons.cancel}
              buttonType={RoundedAppButtonType.Solid}
              buttonContainerStyle={styles.buttonContainer}
              onPress={onExploreNowClick}
              textStyle={styles.buttonText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(92,92,101,0.50)',
  },
  modalView: {
    width: modalWidth,
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m3,
    paddingBottom: Styles.Spacing.m4,
  },
  cancelButton: {
    position: 'absolute',
    justifyContent: 'flex-end',
    right: Styles.Heights.h1,
    top: Styles.Heights.h1,
  },
  topTitleView: {
    marginTop: Styles.Spacing.m4 + Styles.Spacing.m2,
  },
  iconView: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Styles.Spacing.m3,
  },
  text: {},
  titleText: {
    marginHorizontal: Styles.Spacing.m2,
    fontSize: getLayoutDescriber().subHeaderFontSize(),
    fontWeight: '700',
    textAlign: 'center',
    color: Styles.Colors.PayNew.black01,
  },
  subTitleText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    textAlign: 'center',
    marginHorizontal: Styles.Spacing.m2,
    color: Styles.Colors.PayNew.black01,
    marginTop: Styles.Spacing.m2,
  },
  descriptionText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '400',
    textAlign: 'center',
    color: Styles.Colors.PayNew.black01,
    marginTop: Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m3,
  },
  image: {
    ...Platform.select({
      web: {
        width: screenWidth * 0.24,
        height: screenWidth * 0.24 * 1.4,
      },
      default: {
        width: screenWidth * 0.58,
        height: screenWidth * 0.58 * 1.4,
      },
    }),
  },
  buttonContainer: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
    alignSelf: 'center',
  },
  buttonText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    textTransform: 'none',
    paddingHorizontal: Styles.Spacing.m2,
  },
});

export default MoreWaysToEarnDetailsDialogue;
